type AnyGetter<T, R> = (this: T) => R;

/**
 * Cache a getter result per-instance.
 * - Stores cache in a non-enumerable, per-decorator Symbol to avoid collisions.
 * - Works with TS 5.x / new decorators (ClassGetterDecoratorContext).
 * - Pure getters only: if underlying state changes, you must provide invalidation yourself.
 */
export function cached<T extends object, R>(originalGetter: AnyGetter<T, R>, context: ClassGetterDecoratorContext<T, R>): AnyGetter<T, R> {
  if (context.kind !== 'getter') {
    throw new TypeError('@cached can only be applied to a getter.');
  }

  // One unique key per decorated getter (per class definition).
  const cacheKey = Symbol(`@cached:${String(context.name)}`);

  return function (this: T): R {
    // Use hasOwnProperty so prototype chain doesn't interfere.
    if (Object.prototype.hasOwnProperty.call(this, cacheKey)) {
      return (this as any)[cacheKey] as R;
    }

    const value = originalGetter.call(this);

    // Define as non-enumerable to avoid polluting iteration/serialization/debug output.
    Object.defineProperty(this, cacheKey, {
      value,
      writable: true, // allow manual override if needed
      configurable: true, // allow manual deletion to invalidate
      enumerable: false,
    });

    return value;
  };
}
